// GenerateDocumentCard — ad-hoc document generation for one matter.
//
// Substep 5-π.3: this was ~900 lines living inside MatterDetailPage,
// rendered at the bottom of the Documents tab, while the sidebar's
// "Generate Doc" nav item was a second copy of the matters list whose
// only job was to bounce you into that tab. The nav item promised
// generation and delivered a list.
//
// Now it is a component, and GeneratePage renders it under a matter
// picker — pick a matter, generate any template. The matter page keeps
// stage-driven generation (what should I send at this stage?); this is
// the ad-hoc path (I need this specific document).
//
// Everything it needs is its own: templates, the matter's Clio
// providers, subrogation contacts, PDF availability, the missing-fields
// prompt, the directory batch picker, the generation log, and the
// 60-second undo window for both the Clio upload and the field
// writeback.

import React, { useEffect, useState } from "react";
import { C, UNDO_WINDOW_SECONDS } from "../constants.js";
import { I } from "../icons.jsx";
import { API, apiFetch, cachedFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function GenerateDocumentCard({ matter, showNotification }) {
  // ── Template selection ──────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateCatFilter, setTemplateCatFilter] = useState("");
  const [templateExpandedCategories, setTemplateExpandedCategories] = useState({});

  // ── Provider / subrogation pickers ──────────────────────────────
  const [providers, setProviders] = useState([]);
  const [subrogationContacts, setSubrogationContacts] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedSubContact, setSelectedSubContact] = useState(null);

  // Directory batch: generate the same template for N providers picked
  // out of the shared directory rather than the matter's own roster.
  const [showDirBatchModal, setShowDirBatchModal] = useState(false);
  const [dirBatchProviders, setDirBatchProviders] = useState([]);
  const [dirBatchDirectory, setDirBatchDirectory] = useState([]);
  const [dirBatchCategories, setDirBatchCategories] = useState([]);
  const [dirBatchStates, setDirBatchStates] = useState([]);
  const [dirBatchSearch, setDirBatchSearch] = useState("");
  const [dirBatchCat, setDirBatchCat] = useState("");
  const [dirBatchState, setDirBatchState] = useState("");

  // ── Output + run state ──────────────────────────────────────────
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const [outputFormat, setOutputFormat] = useState("docx");
  const [generating, setGenerating] = useState(false);
  const [genLog, setGenLog] = useState([]);
  const [genComplete, setGenComplete] = useState(false);
  const [genError, setGenError] = useState(null);
  const [genResults, setGenResults] = useState(null);

  // ── Missing-fields prompt ───────────────────────────────────────
  const [showMissingModal, setShowMissingModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [fieldOverrides, setFieldOverrides] = useState({});
  const [writeBackToClio, setWriteBackToClio] = useState(true);

  // ── Undo window ─────────────────────────────────────────────────
  const [recentGenAt, setRecentGenAt] = useState(null);
  const [undoneDocIds, setUndoneDocIds] = useState(() => new Set());
  const [undoingDocId, setUndoingDocId] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [writebackUndone, setWritebackUndone] = useState(false);
  const [undoingWriteback, setUndoingWriteback] = useState(false);

  // Everything this card needs, loaded once per matter. Templates,
  // subrogation contacts and /health ride the shared TTL cache — they
  // are the same for every matter.
  useEffect(() => {
    setSelectedTemplate(null);
    setSelectedProvider(null);
    setSelectedSubContact(null);
    setDirBatchProviders([]);
    setGenLog([]);
    setGenComplete(false);
    setGenError(null);
    setGenResults(null);
    if (!matter?.id) return;

    cachedFetch("/api/templates")
      .then(d => setTemplates(d.data || []))
      .catch(e => console.error("API call failed:", e));

    apiFetch(`/api/matters/${matter.id}`)
      .then(d => setProviders((d.data || d)?.providers || []))
      .catch(e => console.error("API call failed:", e));

    cachedFetch("/api/subrogation-contacts", 300_000)
      .then(d => setSubrogationContacts(d.data || []))
      .catch(e => console.error("API call failed:", e));

    cachedFetch("/health", 600_000)
      .then(d => setPdfAvailable(!!d.pdf_available))
      .catch(e => console.error("API call failed:", e));
  }, [matter?.id]);

  // ── Undo Upload window management ───────────────────────────────
  // When genResults transitions to a new successful result, start the
  // 60-second undo window. Clear undo state when genResults is null
  // (user navigated away from the result).
  useEffect(() => {
    if (genResults && (genResults.documents || []).length > 0) {
      setRecentGenAt(Date.now());
      setUndoneDocIds(new Set());
      setWritebackUndone(false);
    } else {
      setRecentGenAt(null);
    }
  }, [genResults]);

  const handleUndoWriteback = async () => {
    const undoEventId = genResults?.clio_writeback?.undo_event_id;
    if (!undoEventId) return;
    setUndoingWriteback(true);
    try {
      await apiFetch(`/api/audit/${undoEventId}/undo`, { method: "POST" });
      setWritebackUndone(true);
      showNotification(
        "Writeback undone — the matter's custom fields are back to their previous values.",
        "warning",
      );
    } catch (e) {
      showNotification("Failed to undo writeback: " + e.message, "error");
    } finally {
      setUndoingWriteback(false);
    }
  };

  // Tick once a second while the undo window is open so the countdown
  // updates. Stops once the window closes — no wasted re-renders.
  useEffect(() => {
    if (!recentGenAt) return;
    const remaining = UNDO_WINDOW_SECONDS - Math.floor((Date.now() - recentGenAt) / 1000);
    if (remaining <= 0) return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [recentGenAt]);

  const undoSecondsRemaining = recentGenAt
    ? Math.max(0, UNDO_WINDOW_SECONDS - Math.floor((nowTick - recentGenAt) / 1000))
    : 0;

  const handleUndoUpload = async (docId) => {
    if (!docId) return;
    setUndoingDocId(docId);
    try {
      await apiFetch(`/api/clio/documents/${docId}`, { method: "DELETE" });
      setUndoneDocIds(prev => {
        const next = new Set(prev);
        next.add(docId);
        return next;
      });
      showNotification && showNotification(
        "Upload undone — the document was removed from Clio.",
        "warning",
      );
    } catch (e) {
      showNotification && showNotification(
        "Failed to undo upload: " + e.message,
        "error",
      );
    } finally {
      setUndoingDocId(null);
    }
  };

  const openDirBatchModal = () => {
    apiFetch("/api/providers")
      .then(data => {
        setDirBatchDirectory(data.data || []);
        setDirBatchCategories(data.categories || []);
        setDirBatchStates(data.states || []);
        setDirBatchProviders([]);
        setDirBatchSearch("");
        setDirBatchCat("");
        setDirBatchState("");
        setShowDirBatchModal(true);
      })
      .catch(e => console.error("API call failed:", e));
  };

  const toggleDirBatchProvider = (provider) => {
    setDirBatchProviders(prev => {
      const exists = prev.find(p => p.id === provider.id);
      if (exists) return prev.filter(p => p.id !== provider.id);
      return [...prev, provider];
    });
  };


  const addLog = (msg, type = "info") => {
    setGenLog(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString() }]);
  };

  const runGenerate = async (overrides, opts = {}) => {
    setGenerating(true);
    setGenLog([]);
    setGenComplete(false);
    setGenError(null);
    setGenResults(null);

    addLog("Starting document generation...", "info");
    addLog(`Template: ${selectedTemplate.filename || selectedTemplate.name}`, "template");
    addLog(`Matter: ${matter.id} \u2014 ${matter.client?.name || matter.display_number}`, "data");
    if (overrides && Object.keys(overrides).length > 0) {
      addLog(`Using ${Object.keys(overrides).length} manually entered field(s)`, "merge");
      if (opts.writeBackToClio) {
        addLog(`Will also save these values to Clio after generation`, "merge");
      }
    }

    try {
      addLog(`POST /api/generate`, "api");
      const body = {
        matter_id: matter.id,
        template_filename: selectedTemplate.filename,
        provider_name: selectedProvider || undefined,
        all_providers: selectedProvider === "ALL_PROVIDERS" || dirBatchProviders.length > 0,
        field_overrides: overrides || undefined,
        output_format: outputFormat,
        write_back_to_clio: !!opts.writeBackToClio,
      };
      if (dirBatchProviders.length > 0) {
        body.directory_providers = dirBatchProviders.map(p => ({
          name: p.name, category: p.category, address: p.address, phone: p.phone, fax: p.fax,
        }));
        addLog(`Batch: ${dirBatchProviders.length} providers from directory`, "merge");
      }
      const res = await fetch(`${API}/api/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const result = await res.json();

      // Handle missing fields — show modal instead of failing
      if (res.status === 422 && result.error === "missing_fields") {
        addLog(`Missing ${result.missing.length} required field(s) \u2014 prompting for input`, "info");
        setMissingFields(result.missing || []);
        const defaults = {};
        (result.missing || []).forEach(f => { defaults[f.field] = ""; });
        setFieldOverrides(defaults);
        setShowMissingModal(true);
        setGenerating(false);
        return;
      }

      if (!res.ok) {
        throw new Error(result.detail || result.message || `API error ${res.status}`);
      }

      if (result.success) {
        const docs = result.documents || [];
        docs.forEach(d => {
          addLog(`\u2713 Generated: ${d.filename} (${d.size_bytes} bytes)`, "success");
          if (d.clio_document_id) {
            addLog(`\u2713 Uploaded to Clio (doc ID: ${d.clio_document_id})`, "success");
          }
        });
        // Log post-actions (tasks created, completed)
        const pa = result.post_actions || [];
        pa.forEach(a => {
          if (a.action === "create_followup_task") {
            addLog(`\u2713 Created task: "${a.task_name}" (due ${a.due_at})`, "success");
          } else if (a.action === "mark_task_complete") {
            addLog(`\u2713 Completed task: "${a.task_name}"`, "success");
          }
        });
        // Surface Clio writeback results when the user opted in
        const wb = result.clio_writeback;
        if (wb && (wb.written?.length || wb.errors?.length || wb.skipped?.length)) {
          (wb.written || []).forEach(w => {
            addLog(`✓ Saved to Clio: ${w.clio_field_name} = ${w.value}`, "success");
          });
          (wb.skipped || []).forEach(s => {
            addLog(`Skipped writeback: ${s.field} (${s.reason})`, "info");
          });
          (wb.errors || []).forEach(e => {
            const msg = typeof e === "string" ? e : (e.error || JSON.stringify(e));
            addLog(`Writeback error: ${msg}`, "error");
          });
        }

        addLog(`Done in ${result.generation_time_ms}ms`, "success");
        setGenResults(result);
        setGenComplete(true);
        const taskCount = pa.filter(a => a.action === "create_followup_task").length;
        const writtenCount = wb?.written?.length || 0;
        let msg = "Document generated and attached to Clio";
        if (writtenCount > 0) {
          msg += `. Saved ${writtenCount} field${writtenCount === 1 ? "" : "s"} back to the matter.`;
        }
        if (taskCount > 0) {
          msg += ` ${taskCount} follow-up task(s) created.`;
        }
        showNotification(msg);
      } else {
        throw new Error(result.error || "Generation failed");
      }
    } catch (err) {
      addLog(`\u2717 Error: ${err.message}`, "error");
      setGenError(err.message);
      showNotification(err.message, "error");
    }

    setGenerating(false);
  };

  const handleGenerate = () => {
    if (!selectedTemplate) return;
    // If a subrogation contact is selected, pass its fields as overrides
    if (selectedSubContact) {
      const sc = subrogationContacts.find(c => c.id === selectedSubContact);
      if (sc) {
        const overrides = {
          subrogation_company: sc.company || "",
          subrogation_fax: sc.fax || "",
          subrogation_fax_or_email: sc.fax || sc.email || "",
        };
        runGenerate(overrides);
        return;
      }
    }
    runGenerate(null);
  };

  const handleSubmitOverrides = () => {
    setShowMissingModal(false);
    runGenerate(fieldOverrides, { writeBackToClio });
  };

  const logColors = {
    api: C.infoBorder, data: C.textMuted, template: C.gold,
    merge: "#8B5CF6", success: "#4ADE80", error: "#EF4444", info: C.textMuted,
  };

  return React.createElement(React.Fragment, null,


    // Missing Fields Modal
    showMissingModal && React.createElement("div", {
      role:"dialog", "aria-modal":"true", "aria-label":"Missing Fields",
      style: {
        position:"fixed", inset:0, zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center",
        background:"rgba(0,0,0,0.45)", backdropFilter:"blur(2px)",
      },
      onClick: e => { if (e.target === e.currentTarget) setShowMissingModal(false); },
      onKeyDown: e => { if (e.key === "Escape") setShowMissingModal(false); },
    },
      React.createElement("div", {
        className: "slide-down",
        style: {
          background:"#fff", borderRadius:"14px", padding:"28px 32px", width:"100%", maxWidth:"480px",
          boxShadow:"0 20px 60px rgba(0,0,0,0.3)", maxHeight:"80vh", overflow:"auto",
        }
      },
        React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"6px" } },
          React.createElement("h2", {
            style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:600, color:C.navy }
          }, "Complete Missing Fields"),
          React.createElement("button", {
            onClick: () => setShowMissingModal(false),
            style: { border:"none", background:"none", cursor:"pointer", fontSize:"20px", color:C.textLight, padding:"0 4px" }
          }, "\u00d7"),
        ),
        React.createElement("p", {
          style: { fontSize:"13px", color:C.textMuted, marginBottom:"20px", lineHeight:1.5 }
        }, "These fields aren't set in Clio yet. Type values below to include them in the document, or leave blank. You can also add this data to the matter in Clio and regenerate later."),
        missingFields.map(f => {
          // Only `custom_field` source maps cleanly back to Clio. Surface
          // a small "(custom field — will save to Clio)" hint so the user
          // knows which fields the writeback toggle applies to.
          const writable = f.source === "custom_field";
          return React.createElement("div", { key: f.field, style: { marginBottom:"14px" } },
            React.createElement("label", {
              style: { fontSize:"11px", color:C.textMuted, fontWeight:600, display:"block", marginBottom:"6px", textTransform:"uppercase", letterSpacing:"0.04em" }
            }, f.label || f.field),
            React.createElement("input", {
              type: f.field.includes("date") ? "date" : "text",
              value: fieldOverrides[f.field] || "",
              onChange: e => setFieldOverrides(prev => ({ ...prev, [f.field]: e.target.value })),
              placeholder: f.label || f.field,
              style: {
                width:"100%", padding:"10px 14px", borderRadius:"8px",
                border:`1px solid ${C.warmGray}`, fontSize:"14px",
                color:C.textDark, background:"#fff",
              }
            }),
            React.createElement("div", {
              style: { fontSize:"11px", color:C.textLight, marginTop:"4px" }
            },
              "Source: ", f.source || "unknown",
              writable && writeBackToClio && React.createElement("span", {
                style: { color: C.gold, fontWeight: 500, marginLeft: "6px" }
              }, "→ will save to Clio"),
              !writable && writeBackToClio && React.createElement("span", {
                style: { color: C.textMuted, marginLeft: "6px" }
              }, "(this document only — not a Clio matter field)"),
            ),
          );
        }),

        // Writeback toggle. Default ON because the firm typically wants
        // these values to persist; only uncheck for one-off / test data.
        React.createElement("label", {
          style: {
            display: "flex", alignItems: "flex-start", gap: "10px",
            padding: "12px 14px", borderRadius: "8px",
            background: writeBackToClio ? `${C.gold}10` : C.cream,
            border: `1px solid ${writeBackToClio ? C.gold + "40" : C.warmGray}`,
            cursor: "pointer", marginTop: "8px",
          },
        },
          React.createElement("input", {
            type: "checkbox",
            checked: writeBackToClio,
            onChange: e => setWriteBackToClio(e.target.checked),
            style: { marginTop: "3px", cursor: "pointer" },
          }),
          React.createElement("div", null,
            React.createElement("div", {
              style: { fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "2px" }
            }, "Save these values to Clio"),
            React.createElement("div", {
              style: { fontSize: "12px", color: C.textMuted, lineHeight: 1.4 }
            },
              "Writes the values you typed back to the matter's custom fields ",
              "so the next document for this matter has them auto-populated. ",
              "Only fields with source ",
              React.createElement("strong", null, "custom_field"),
              " can be written back; others apply to this document only.",
            ),
          ),
        ),

        React.createElement("div", { style: { display:"flex", gap:"10px", justifyContent:"flex-end", marginTop:"20px", paddingTop:"16px", borderTop:`1px solid ${C.warmGray}60` } },
          React.createElement("button", {
            onClick: () => setShowMissingModal(false),
            style: {
              padding:"10px 20px", borderRadius:"10px", border:`1px solid ${C.warmGray}`,
              background:"#fff", color:C.textMuted, fontSize:"13px", fontWeight:600, cursor:"pointer",
            }
          }, "Cancel"),
          React.createElement("button", {
            onClick: handleSubmitOverrides,
            style: {
              padding:"10px 20px", borderRadius:"10px", border:"none",
              background:C.navy, color:"#fff", fontSize:"13px", fontWeight:600, cursor:"pointer",
              display:"flex", alignItems:"center", gap:"6px",
            }
          }, I.zap, writeBackToClio ? " Generate & save to Clio" : " Generate with overrides"),
        ),
      ),
    ),

    // ============ Directory Batch Provider Modal ============
    showDirBatchModal && React.createElement("div", {
      role:"dialog", "aria-modal":"true", "aria-label":"Select Providers",
      style: {
        position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.5)",
        display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000,
      },
      onClick: e => { if (e.target === e.currentTarget) setShowDirBatchModal(false); },
      onKeyDown: e => { if (e.key === "Escape") setShowDirBatchModal(false); },
    },
      React.createElement("div", {
        style: {
          background:"#fff", borderRadius:"16px", padding:"28px 32px", width:"640px", maxHeight:"80vh",
          display:"flex", flexDirection:"column", boxShadow:"0 20px 60px rgba(0,0,0,0.3)",
        }
      },
        React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" } },
          React.createElement("h2", { style: { fontFamily:"'Playfair Display',serif", fontSize:"20px", fontWeight:600, color:C.navy } },
            "Select Providers from Directory"
          ),
          React.createElement("button", {
            onClick: () => setShowDirBatchModal(false),
            style: { border:"none", background:"none", fontSize:"20px", cursor:"pointer", color:C.textMuted, padding:"4px" }
          }, "\u00d7"),
        ),
        dirBatchProviders.length > 0 && React.createElement("div", {
          style: { padding:"8px 12px", borderRadius:"8px", background:`${C.gold}08`, border:`1px solid ${C.gold}30`, marginBottom:"12px", fontSize:"13px", fontWeight:600, color:C.gold }
        }, `${dirBatchProviders.length} provider(s) selected`),
        // Search + Filter
        React.createElement("div", { style: { display:"flex", gap:"10px", marginBottom:"12px", flexWrap:"wrap" } },
          React.createElement("input", {
            type: "text",
            placeholder: "Search providers...",
            value: dirBatchSearch,
            onChange: e => setDirBatchSearch(e.target.value),
            style: { flex:1, minWidth:"160px", padding:"10px 14px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, fontSize:"14px", outline:"none" }
          }),
          React.createElement("select", {
            value: dirBatchState,
            onChange: e => setDirBatchState(e.target.value),
            style: { padding:"10px 14px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, fontSize:"14px", background:"#fff", cursor:"pointer" }
          },
            React.createElement("option", { value:"" }, "All States"),
            dirBatchStates.map(st => React.createElement("option", { key: st, value: st }, st)),
          ),
          React.createElement("select", {
            value: dirBatchCat,
            onChange: e => setDirBatchCat(e.target.value),
            style: { padding:"10px 14px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, fontSize:"14px", background:"#fff", cursor:"pointer" }
          },
            React.createElement("option", { value:"" }, "All Categories"),
            dirBatchCategories.map(cat => React.createElement("option", { key: cat, value: cat }, cat)),
          ),
        ),
        // Provider list with checkboxes
        React.createElement("div", { style: { overflowY:"auto", flex:1, maxHeight:"45vh" } },
          dirBatchDirectory
            .filter(p => !dirBatchSearch || p.name.toLowerCase().includes(dirBatchSearch.toLowerCase())
              || (p.facility || "").toLowerCase().includes(dirBatchSearch.toLowerCase())
              || (p.city || "").toLowerCase().includes(dirBatchSearch.toLowerCase()))
            .filter(p => !dirBatchCat || p.category === dirBatchCat)
            .filter(p => !dirBatchState || p.state === dirBatchState)
            .map(p => {
              const isSelected = dirBatchProviders.some(dp => dp.id === p.id);
              return React.createElement("div", {
                key: p.id,
                onClick: () => toggleDirBatchProvider(p),
                style: {
                  padding:"10px 14px", borderRadius:"8px",
                  border: `1.5px solid ${isSelected ? C.gold : C.warmGray}`,
                  marginBottom:"6px", display:"flex", alignItems:"center", gap:"10px", cursor:"pointer",
                  background: isSelected ? `${C.gold}08` : "#fff",
                  transition:"all 0.15s",
                },
              },
                React.createElement("input", {
                  type: "checkbox", checked: isSelected, readOnly: true,
                  style: { width:16, height:16, accentColor:C.gold, cursor:"pointer", flexShrink:0 },
                }),
                React.createElement("div", { style: { flex:1, minWidth:0 } },
                  React.createElement("div", { style: { fontSize:"14px", fontWeight:600, color:C.navy } }, p.name),
                  React.createElement("div", { style: { fontSize:"11px", color:C.gold, fontWeight:500 } },
                    p.category,
                    (p.city || p.state) ? " \u2014 " + [p.city, p.state].filter(Boolean).join(", ") : "",
                  ),
                  p.address && React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"2px" } }, p.address),
                ),
              );
            }),
        ),
        // Confirm button
        React.createElement("div", { style: { display:"flex", gap:"10px", justifyContent:"flex-end", marginTop:"16px", paddingTop:"14px", borderTop:`1px solid ${C.warmGray}60` } },
          React.createElement("button", {
            onClick: () => { setDirBatchProviders([]); },
            style: {
              padding:"10px 20px", borderRadius:"10px", border:`1px solid ${C.warmGray}`,
              background:"#fff", color:C.textMuted, fontSize:"13px", fontWeight:600, cursor:"pointer",
            }
          }, "Clear All"),
          React.createElement("button", {
            onClick: () => { setSelectedProvider(null); setShowDirBatchModal(false); },
            disabled: dirBatchProviders.length === 0,
            style: {
              padding:"10px 20px", borderRadius:"10px", border:"none",
              background: dirBatchProviders.length === 0 ? C.warmGray : C.navy,
              color: dirBatchProviders.length === 0 ? C.textLight : "#fff",
              fontSize:"13px", fontWeight:600, cursor: dirBatchProviders.length === 0 ? "default" : "pointer",
              display:"flex", alignItems:"center", gap:"6px",
            }
          }, I.check, ` Use ${dirBatchProviders.length} Provider(s)`),
        ),
      ),
    ),

    React.createElement("div", {
      style: { display:"grid", gridTemplateColumns: genLog.length > 0 ? "1fr 1fr" : "1fr", gap:"20px" }
    },
      // Template Selection
      React.createElement("div", {
        style: { background:"#fff", borderRadius:"12px", padding:"24px 28px", border:`1px solid ${C.warmGray}` }
      },
        React.createElement("h2", {
          style: { fontSize:"13px", color:C.textMuted, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"16px" }
        }, "Generate Document"),

        React.createElement("div", { style: { display:"flex", gap:"8px", marginBottom:"12px" } },
          React.createElement("input", {
            type: "text",
            placeholder: "Search templates...",
            value: templateSearch || "",
            onChange: e => setTemplateSearch(e.target.value),
            style: {
              flex:1, padding:"8px 12px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
              fontSize:"13px", outline:"none",
            }
          }),
          React.createElement("select", {
            value: templateCatFilter || "",
            onChange: e => setTemplateCatFilter(e.target.value),
            style: {
              padding:"8px 12px", borderRadius:"8px", border:`1px solid ${C.warmGray}`,
              fontSize:"13px", background:"#fff", cursor:"pointer",
            }
          },
            React.createElement("option", { value:"" }, "All Categories"),
            [...new Set(templates.map(t => t.category).filter(Boolean))].sort().map(cat =>
              React.createElement("option", { key: cat, value: cat }, cat)
            ),
          ),
        ),

        (() => {
          const filtered = templates
            .filter(t => !templateSearch || (t.name || t.filename).toLowerCase().includes(templateSearch.toLowerCase()))
            .filter(t => !templateCatFilter || t.category === templateCatFilter);
          const filterActive = !!(templateSearch || templateCatFilter);

          // ----- Render a single template button (used by both flat
          // search-result mode and accordion mode).
          const renderTemplateBtn = (tmpl, i) => React.createElement("button", {
            key: tmpl.filename || i,
            onClick: () => { setSelectedTemplate(tmpl); setSelectedProvider(null); setSelectedSubContact(null); setGenLog([]); setGenComplete(false); setGenError(null); setGenResults(null); },
            style: {
              width: "100%", padding: "12px 16px", borderRadius: "8px",
              border: `1.5px solid ${selectedTemplate?.filename === tmpl.filename ? C.gold : C.warmGray}`,
              background: selectedTemplate?.filename === tmpl.filename ? `${C.gold}08` : "#fff",
              cursor: "pointer", textAlign: "left", marginBottom: "8px", transition: "all 0.15s",
            },
          },
            React.createElement("div", { style: { fontSize: "14px", fontWeight: 600, color: C.navy, display: "flex", alignItems: "center", gap: "8px" } },
              selectedTemplate?.filename === tmpl.filename && React.createElement("span", { style: { color: C.gold } }, I.check),
              tmpl.name || tmpl.filename,
            ),
            React.createElement("div", { style: { fontSize: "12px", color: C.textMuted, marginTop: "2px", marginLeft: selectedTemplate?.filename === tmpl.filename ? "24px" : 0 } },
              tmpl.filename, tmpl.requires_provider && " \u00b7 Requires provider",
            ),
          );

          if (filtered.length === 0) {
            return React.createElement("div", {
              style: { padding: "16px", textAlign: "center", color: C.textMuted, fontSize: "13px" }
            }, "No templates match your search.");
          }

          // ----- Search/filter mode: flat list of matches (no accordion)
          if (filterActive) {
            return React.createElement(React.Fragment, null,
              React.createElement("div", {
                style: { fontSize: "11px", color: C.textMuted, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em" }
              }, `${filtered.length} match${filtered.length === 1 ? "" : "es"}`),
              ...filtered.map(renderTemplateBtn),
            );
          }

          // ----- Accordion mode: group by category, all collapsed by default
          const grouped = {};
          filtered.forEach(t => {
            const cat = t.category || "Other";
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(t);
          });
          // Sort categories alphabetically; push "Other" to the end
          // since it's a catch-all.
          const categoryOrder = Object.keys(grouped).sort();
          const otherIdx = categoryOrder.indexOf("Other");
          if (otherIdx >= 0) {
            categoryOrder.splice(otherIdx, 1);
            categoryOrder.push("Other");
          }

          // If exactly one category exists, skip the accordion shell \u2014
          // it's just visual noise. Render the templates flat.
          if (categoryOrder.length === 1) {
            return React.createElement(React.Fragment, null,
              ...filtered.map(renderTemplateBtn),
            );
          }

          return React.createElement(React.Fragment, null,
            ...categoryOrder.map(cat => {
              const items = grouped[cat];
              const isExpanded = !!templateExpandedCategories[cat];
              // If the currently selected template is inside this
              // category, force-expand so the user can see what's
              // selected without an extra click.
              const containsSelected = selectedTemplate && items.some(t => t.filename === selectedTemplate.filename);
              const open = isExpanded || containsSelected;
              return React.createElement("div", {
                key: cat,
                style: {
                  marginBottom: "6px", borderRadius: "8px",
                  border: `1px solid ${open ? C.warmGray : C.warmGray + "80"}`,
                  background: open ? "#fff" : "transparent",
                  overflow: "hidden",
                },
              },
                React.createElement("button", {
                  onClick: () => setTemplateExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] })),
                  style: {
                    width: "100%", padding: "10px 14px", display: "flex",
                    alignItems: "center", gap: "10px",
                    background: "transparent", border: "none", cursor: "pointer",
                    textAlign: "left",
                  },
                  onMouseEnter: e => { e.currentTarget.style.background = `${C.gold}06`; },
                  onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
                },
                  React.createElement("span", {
                    style: {
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      width: "16px", height: "16px", color: C.textMuted,
                      transition: "transform 0.15s",
                      transform: open ? "rotate(90deg)" : "rotate(0deg)",
                    },
                  }, I.chevron),
                  React.createElement("span", {
                    style: { flex: 1, fontSize: "13px", fontWeight: 600, color: C.navy }
                  }, cat),
                  React.createElement("span", {
                    style: {
                      fontSize: "11px", fontWeight: 600, color: C.textMuted,
                      background: C.warmGray + "60", padding: "2px 8px", borderRadius: "9999px",
                    },
                  }, items.length),
                ),
                open && React.createElement("div", { style: { padding: "0 10px 4px" } },
                  ...items.map(renderTemplateBtn),
                ),
              );
            }),
          );
        })(),

        // Provider selector
        selectedTemplate?.requires_provider && React.createElement("div", { style: { marginTop:"16px" } },
          React.createElement("label", { style: { fontSize:"12px", color:C.textMuted, fontWeight:500, display:"block", marginBottom:"8px" } }, "Select Provider"),
          providers.length > 0 && React.createElement("select", {
            value: selectedProvider || "",
            onChange: e => { setSelectedProvider(e.target.value); setDirBatchProviders([]); },
            style: {
              width:"100%", padding:"10px 14px", borderRadius:"8px",
              border:`1px solid ${C.warmGray}`, fontSize:"14px", color:C.textDark, background:"#fff", cursor:"pointer",
            },
          },
            React.createElement("option", { value:"" }, "Choose a provider..."),
            React.createElement("option", { value:"ALL_PROVIDERS" }, "ALL PROVIDERS (batch)"),
            providers.map((p, i) =>
              React.createElement("option", { key:i, value: p.medical_provider?.name || p.description || `Provider ${i+1}` },
                p.medical_provider?.name || p.description || `Provider ${i+1}`
              )
            ),
          ),
          // "Select from Directory" button
          React.createElement("button", {
            onClick: openDirBatchModal,
            style: {
              marginTop:"8px", padding:"8px 14px", borderRadius:"8px",
              border:`1px solid ${C.navy}`, background:"transparent", color:C.navy,
              fontSize:"12px", fontWeight:600, cursor:"pointer", display:"flex", alignItems:"center", gap:"6px",
            }
          }, I.search, " Select from Provider Directory"),
          // Show selected directory providers badge
          dirBatchProviders.length > 0 && React.createElement("div", {
            style: { marginTop:"8px", padding:"10px 14px", borderRadius:"8px", background:`${C.gold}08`, border:`1px solid ${C.gold}30` }
          },
            React.createElement("div", { style: { fontSize:"12px", fontWeight:600, color:C.gold, marginBottom:"4px" } },
              `${dirBatchProviders.length} provider(s) selected from directory`
            ),
            dirBatchProviders.map((p, i) =>
              React.createElement("span", {
                key: i,
                style: {
                  display:"inline-flex", alignItems:"center", gap:"4px",
                  padding:"2px 8px", borderRadius:"4px", background:C.warmWhite,
                  fontSize:"11px", color:C.navy, fontWeight:500, marginRight:"4px", marginBottom:"4px",
                }
              },
                p.name,
                React.createElement("button", {
                  onClick: () => setDirBatchProviders(prev => prev.filter(pp => pp.id !== p.id)),
                  style: { border:"none", background:"none", cursor:"pointer", color:C.textLight, fontSize:"12px", padding:"0 2px" }
                }, "\u00d7"),
              )
            ),
            React.createElement("button", {
              onClick: () => setDirBatchProviders([]),
              style: {
                display:"block", marginTop:"6px", border:"none", background:"none",
                color:C.textMuted, fontSize:"11px", cursor:"pointer", textDecoration:"underline",
              }
            }, "Clear all"),
          ),
        ),

        // Subrogation contact selector (for templates with subrogation fields)
        selectedTemplate && (selectedTemplate.fields || []).includes("subrogation_company") && subrogationContacts.length > 0 && React.createElement("div", { style: { marginTop:"16px" } },
          React.createElement("label", { style: { fontSize:"12px", color:C.textMuted, fontWeight:500, display:"block", marginBottom:"8px" } }, "Select Subrogation Contact (Optional)"),
          React.createElement("select", {
            value: selectedSubContact || "",
            onChange: e => setSelectedSubContact(e.target.value || null),
            style: {
              width:"100%", padding:"10px 14px", borderRadius:"8px",
              border:`1px solid ${C.warmGray}`, fontSize:"14px", color:C.textDark, background:"#fff", cursor:"pointer",
            },
          },
            React.createElement("option", { value:"" }, "Manual entry / skip..."),
            subrogationContacts.map((sc, i) =>
              React.createElement("option", { key: sc.id || i, value: sc.id },
                sc.company + (sc.type ? ` (${sc.type})` : "") + (sc.state ? ` — ${sc.state}` : "")
              )
            ),
          ),
          selectedSubContact && (() => {
            const sc = subrogationContacts.find(c => c.id === selectedSubContact);
            return sc && React.createElement("div", {
              style: { marginTop:"8px", padding:"10px 12px", borderRadius:"8px", background:`${C.gold}08`, border:`1px solid ${C.gold}30`, fontSize:"12px", color:C.textMuted, lineHeight:1.6 }
            },
              sc.address && React.createElement("div", null, "Address: ", sc.address),
              sc.fax && React.createElement("div", null, "Fax: ", sc.fax),
              sc.phone && React.createElement("div", null, "Phone: ", sc.phone),
              sc.email && React.createElement("div", null, "Email: ", sc.email),
            );
          })(),
        ),

        // PDF Output option
        pdfAvailable && selectedTemplate && React.createElement("label", {
          style: {
            display:"flex", alignItems:"center", gap:"8px", marginTop:"14px",
            fontSize:"13px", color:C.textMuted, cursor:"pointer",
          }
        },
          React.createElement("input", {
            type: "checkbox",
            checked: outputFormat === "pdf",
            onChange: e => setOutputFormat(e.target.checked ? "pdf" : "docx"),
            style: { width:16, height:16, cursor:"pointer" },
          }),
          "Generate as PDF",
        ),

        // Generate Button
        React.createElement("button", {
          onClick: handleGenerate,
          disabled: !selectedTemplate || (selectedTemplate.requires_provider && !selectedProvider && dirBatchProviders.length === 0) || generating,
          style: {
            width:"100%", padding:"12px", borderRadius:"10px", border:"none", cursor:"pointer",
            marginTop:"16px",
            background: (!selectedTemplate || (selectedTemplate.requires_provider && !selectedProvider && dirBatchProviders.length === 0))
              ? C.warmGray : generating ? C.navyLight : C.navy,
            color: (!selectedTemplate || (selectedTemplate.requires_provider && !selectedProvider && dirBatchProviders.length === 0))
              ? C.textLight : "#fff",
            fontSize:"14px", fontWeight:600, display:"flex", alignItems:"center", justifyContent:"center", gap:"8px",
            transition:"all 0.2s",
          }
        },
          generating
            ? React.createElement(React.Fragment, null, React.createElement(Spinner, {size:16,color:"#fff"}), " Generating...")
            : genComplete
              ? React.createElement(React.Fragment, null, I.check, " Generated \u2014 Generate Another?")
              : dirBatchProviders.length > 0
                ? React.createElement(React.Fragment, null, I.zap, ` Generate for ${dirBatchProviders.length} Providers`)
                : React.createElement(React.Fragment, null, I.zap, " Generate Document"),
        ),
      ),

      // Batch Results Summary
      genComplete && genResults && (genResults.documents || []).length > 1 && React.createElement("div", {
        className: "fade-in",
        style: {
          background:"#fff", borderRadius:"12px", padding:"24px 28px",
          border:`1px solid ${C.warmGray}`, marginBottom:"16px",
        }
      },
        React.createElement("div", {
          style: { display:"flex", alignItems:"center", gap:"10px", marginBottom:"16px" }
        },
          React.createElement("div", {
            style: { width:36, height:36, borderRadius:"50%", background:C.successBg, display:"flex", alignItems:"center", justifyContent:"center", color:C.success }
          }, I.check),
          React.createElement("div", { style: { flex: 1 } },
            React.createElement("div", {
              style: { fontSize:"15px", fontWeight:600, color:C.navy }
            }, `Batch Complete \u2014 ${genResults.documents.length} Documents`),
            React.createElement("div", {
              style: { fontSize:"12px", color:C.textMuted }
            }, `Generated in ${genResults.generation_time_ms}ms`),
          ),
          // "Undo all" master button \u2014 only shown when there are 2+
          // docs AND the undo window is still open AND at least one
          // doc hasn't already been undone individually. For the
          // records-packet flow (N providers = N docs) this is the
          // "I clicked Generate on the wrong matter" single-click
          // reversal. Iterates the still-undoable docs and fires the
          // DELETE for each.
          (() => {
            const undoable = (genResults.documents || [])
              .map(d => d.clio_document_id)
              .filter(id => id && !undoneDocIds.has(id));
            if (undoable.length < 2 || undoSecondsRemaining <= 0) return null;
            const busy = undoable.some(id => id === undoingDocId);
            return React.createElement("button", {
              onClick: async () => {
                // Sequential rather than parallel so the audit log
                // captures one event per doc, and so a partial
                // failure doesn't leave the user wondering which
                // half succeeded.
                for (const id of undoable) {
                  await handleUndoUpload(id);
                }
              },
              disabled: busy,
              title: `Wrong matter? Click to delete all ${undoable.length} docs from Clio.`,
              style: {
                fontSize: "12px", fontWeight: 600,
                padding: "6px 14px", borderRadius: "8px",
                background: busy ? C.warmGray : "#fff",
                color: busy ? C.textMuted : C.error,
                border: `1px solid ${C.error}40`,
                cursor: busy ? "not-allowed" : "pointer",
                flexShrink: 0,
                opacity: busy ? 0.6 : 1,
              },
            }, busy ? "Undoing all\u2026" : `Undo all (${undoSecondsRemaining}s)`);
          })(),
        ),
        React.createElement("div", {
          style: { border:`1px solid ${C.warmGray}`, borderRadius:"8px", overflow:"hidden" }
        },
          genResults.documents.map((doc, i) => {
            const docId = doc.clio_document_id;
            const wasUndone = docId && undoneDocIds.has(docId);
            const canUndo = !!docId && !wasUndone && undoSecondsRemaining > 0;
            const isUndoing = undoingDocId === docId;
            return React.createElement("div", {
              key: i,
              style: {
                display:"flex", alignItems:"center", gap:"10px", padding:"10px 14px",
                borderBottom: i < genResults.documents.length - 1 ? `1px solid ${C.warmGray}30` : "none",
                fontSize:"13px",
                opacity: wasUndone ? 0.55 : 1,
              }
            },
              React.createElement("span", {
                style: { color: wasUndone ? C.textMuted : C.success, flexShrink:0 },
              }, wasUndone ? I.back : I.check),
              React.createElement("span", {
                style: {
                  fontWeight:500, color: wasUndone ? C.textMuted : C.navy,
                  flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  textDecoration: wasUndone ? "line-through" : "none",
                },
              }, doc.filename),
              React.createElement("span", {
                style: { fontSize:"11px", color:C.textLight, flexShrink:0 },
              }, `${Math.round(doc.size_bytes / 1024)} KB`),
              wasUndone
                ? React.createElement("span", {
                    style: { fontSize:"11px", color:C.textMuted, fontWeight:600, flexShrink:0 },
                  }, "Removed")
                : canUndo
                  ? React.createElement("button", {
                      onClick: () => handleUndoUpload(docId),
                      disabled: isUndoing,
                      title: `Wrong matter? Click to delete this upload from Clio. Available for ${UNDO_WINDOW_SECONDS} seconds after generation.`,
                      style: {
                        fontSize:"11px", fontWeight:600,
                        padding:"4px 10px", borderRadius:"6px",
                        background: isUndoing ? C.warmGray : "#fff",
                        color: isUndoing ? C.textMuted : C.error,
                        border:`1px solid ${C.error}40`,
                        cursor: isUndoing ? "not-allowed" : "pointer",
                        flexShrink:0,
                        opacity: isUndoing ? 0.6 : 1,
                      },
                    },
                      isUndoing
                        ? "Undoing…"
                        : `Undo (${undoSecondsRemaining}s)`,
                    )
                  : React.createElement("span", {
                      style: { fontSize:"11px", color:C.success, fontWeight:600, flexShrink:0 },
                    }, "Uploaded"),
            );
          }),
        ),

        // Writeback summary card — only shown when the missing-fields
        // modal pushed values back to Clio. Carries an Undo button if
        // the backend stashed a snapshot (which it does whenever the
        // write succeeded). Once undone, switches to a muted "Reverted"
        // state — matter custom fields are back to their prior values.
        (genResults.clio_writeback?.written || []).length > 0 && React.createElement("div", {
          style: {
            marginTop: "14px", padding: "12px 14px", borderRadius: "8px",
            background: writebackUndone ? C.warmGray + "30" : `${C.navy}08`,
            border: `1px solid ${writebackUndone ? C.textLight + "40" : C.navy + "20"}`,
            display: "flex", alignItems: "center", gap: "12px",
          },
        },
          React.createElement("div", { style: { flex: 1, minWidth: 0 } },
            React.createElement("div", {
              style: {
                fontSize: "12px", fontWeight: 600,
                color: writebackUndone ? C.textMuted : C.navy,
                marginBottom: "4px",
                textDecoration: writebackUndone ? "line-through" : "none",
              },
            }, writebackUndone
                ? `Writeback reverted — ${genResults.clio_writeback.written.length} field(s) restored to prior values`
                : `Saved ${genResults.clio_writeback.written.length} field(s) back to the matter`),
            React.createElement("div", {
              style: {
                fontSize: "11px", color: C.textMuted,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              },
            }, (genResults.clio_writeback.written || [])
                .map(w => w.clio_field_name || w.field).join(", ")),
          ),
          !writebackUndone
            && genResults.clio_writeback.undo_event_id
            && undoSecondsRemaining > 0
            && React.createElement("button", {
              onClick: handleUndoWriteback,
              disabled: undoingWriteback,
              title: `Wrong matter or wrong values? Click to restore the custom fields. Available for ${UNDO_WINDOW_SECONDS} seconds after generation.`,
              style: {
                fontSize: "11px", fontWeight: 600,
                padding: "5px 12px", borderRadius: "6px",
                background: undoingWriteback ? C.warmGray : "#fff",
                color: undoingWriteback ? C.textMuted : C.error,
                border: `1px solid ${C.error}40`,
                cursor: undoingWriteback ? "not-allowed" : "pointer",
                flexShrink: 0,
                opacity: undoingWriteback ? 0.6 : 1,
              },
            }, undoingWriteback
                ? "Reverting…"
                : `Undo writeback (${undoSecondsRemaining}s)`),
        ),

        // Post-action summary
        (genResults.post_actions || []).length > 0 && React.createElement("div", {
          style: { marginTop:"14px", padding:"12px 14px", borderRadius:"8px", background:`${C.gold}08`, border:`1px solid ${C.gold}30` }
        },
          React.createElement("div", {
            style: { fontSize:"12px", fontWeight:600, color:C.navy, marginBottom:"6px" }
          }, "Follow-up Actions"),
          (genResults.post_actions || []).map((a, i) =>
            React.createElement("div", {
              key: i,
              style: { fontSize:"12px", color:C.textMuted, padding:"2px 0", display:"flex", alignItems:"center", gap:"6px" }
            },
              a.action === "create_followup_task"
                ? React.createElement(React.Fragment, null,
                    React.createElement("span", { style: { color:C.gold } }, I.check),
                    `Task created: "${a.task_name}" \u2014 due ${a.due_at}`
                  )
                : a.action === "mark_task_complete"
                  ? React.createElement(React.Fragment, null,
                      React.createElement("span", { style: { color:C.success } }, I.check),
                      `Task completed: "${a.task_name}"`
                    )
                  : null,
            )
          ),
        ),
      ),

      // API Log Panel
      genLog.length > 0 && React.createElement("div", {
        style: {
          background:C.navyDark, borderRadius:"12px", padding:"20px 24px",
          border:`1px solid ${C.navyLight}`, maxHeight:"500px", overflow:"auto",
        }
      },
        React.createElement("div", {
          style: { fontSize:"11px", color:C.goldLight, textTransform:"uppercase", letterSpacing:"0.08em", fontWeight:600, marginBottom:"14px", display:"flex", alignItems:"center", gap:"6px" }
        },
          React.createElement("span", {
            style: { width:6, height:6, borderRadius:"50%", background: generating ? "#4ADE80" : C.textLight, animation: generating ? "pulse 1s infinite" : "none" }
          }),
          generating ? "Processing..." : genError ? "Failed" : "Complete",
        ),
        genLog.map((log, i) =>
          React.createElement("div", {
            key: i,
            style: {
              fontFamily:"monospace", fontSize:"12px",
              color: logColors[log.type] || "#9CA3AF",
              padding:"3px 0", lineHeight:1.5,
              opacity:0, animation: `fadeIn 0.2s ease-out ${i * 0.05}s forwards`,
            }
          },
            React.createElement("span", { style:{ color:"rgba(255,255,255,0.25)", marginRight:"8px", fontSize:"10px" } }, log.time),
            log.msg,
          )
        ),
      ),
    ),
  );
}
